import { Component, type ErrorInfo, type ReactNode } from 'react';
import { FaExclamationTriangle, FaHome, FaSync } from 'react-icons/fa';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error to console in development
    if (import.meta.env.DEV) {
      console.error('ErrorBoundary caught an error:', error, errorInfo);
    }

    // In production, you could log to an error reporting service
    // Example: logErrorToService(error, errorInfo);

    this.setState({
      error,
      errorInfo,
    });
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="container my-5">
          <div className="row justify-content-center">
            <div className="col-md-8">
              <div className="card border-danger">
                <div className="card-body text-center py-5">
                  <FaExclamationTriangle
                    className="text-danger mb-3"
                    size={64}
                  />
                  <h2 className="card-title">Something went wrong</h2>
                  <p className="card-text text-muted mb-4">
                    We encountered an unexpected error. Please try refreshing
                    the page or return to the homepage.
                  </p>
                  {import.meta.env.DEV && this.state.error && (
                    <details className="text-start mb-4">
                      <summary className="text-danger cursor-pointer">
                        Error Details (Development Only)
                      </summary>
                      <pre className="bg-light p-3 mt-2 rounded small">
                        {this.state.error.toString()}
                        {this.state.errorInfo?.componentStack}
                      </pre>
                    </details>
                  )}
                  <div className="d-flex gap-2 justify-content-center">
                    <button
                      onClick={this.handleReset}
                      className="btn btn-outline-primary"
                    >
                      <FaSync className="me-2" />
                      Try Again
                    </button>
                    <a href="/" className="btn btn-primary">
                      <FaHome className="me-2" />
                      Go Home
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
